import React, {useEffect, useState} from 'react'
import DynamicPlatformSettingNew from './DynamicPlatformSettingNew'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {checkDynamicSettingPair} from 'src/utils/checkDynamicSettingPair'
import useAppToast from 'src/hooks/useAppToast'
import { getAllPlatformTypesAsync, fetchConnectedPlatformAsync } from 'src/redux/actions/platformActions'
import Loader from 'src/utils/Loader'
import PlatformAdditionalMappingGate from './components/PlatformAdditionalMappingGate'
import {isSourceConnectedPlatform} from 'src/utils/platformConnectionValidation'

const DynamicPlatformSettingWrapper: React.FC = () => {
  const [key, setKey] = useState<number>(0)
  const {
    mainStoreData,
    shippingStoreData,
    accountingStoreData,
    erpStoreData,
    omsStoreData,
    ticketStoreData,
    fetchedConnectedPlatformData,
    fetchedConnectedPlatformLoading,
  } = useAppSelector(
    (state) => state.fetchConnectedPlatform
  )
  const {errorToast, successToast, warningToast} = useAppToast()
  const {selectedPlatformId} = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (selectedPlatformId) {
      dispatch(fetchConnectedPlatformAsync())
    }
  }, [dispatch, selectedPlatformId])

   const linkedChannels = async (secondary_platform_id) => {
      const res = await dispatch(getAllPlatformTypesAsync({ platform_id: secondary_platform_id! }));
      const data = res?.payload?.data;
      const selfData = res?.payload?.self_data;
      
      return {
        mainCodes: Array.isArray(data) ? data.map((item) => item.name) : [],
        selectedCode: selfData?.code
      };
    };

  useEffect(() => {
    if (!fetchedConnectedPlatformLoading) {
      // Redirect to platforms page if no eligible source channels are connected
      if (!mainStoreData && !shippingStoreData && !accountingStoreData && !erpStoreData && !omsStoreData) {
        warningToast(
          'Please connect a Source, Accounting, Shipping, ERP, or OMS channel to proceed destination channel settings.',
          {
            theme: 'colored',
            position: 'top-center',
            style: { width: '650px' },
          }
        )
        navigate('/platforms')
        return
      }
      const isBetaRoute = location.pathname.startsWith('/platform-setting-beta')
      if (isBetaRoute) {
        setLoading(false)
        return}

      // Allow access to the dynamic setting page only for declared valid platform combinations
      async function checkAccess() {
        const { mainCodes, selectedCode } = await linkedChannels(selectedPlatformId);
        const hasValidSelection = !!selectedPlatformId;

        const allowAccess =
          hasValidSelection && checkDynamicSettingPair(mainCodes, selectedCode);

        if (!allowAccess) {
          navigate('/listing-channels');
        }
        setLoading(false)
      }
      const selectedConnectedPlatform = fetchedConnectedPlatformData?.find(
        (platform) => platform.id === Number(selectedPlatformId)
      )
      const isKnownSourcePlatform =
        selectedConnectedPlatform != null &&
        isSourceConnectedPlatform(selectedConnectedPlatform)

      if (
        isKnownSourcePlatform ||
        mainStoreData?.id === Number(selectedPlatformId) ||
        accountingStoreData?.id === Number(selectedPlatformId) ||
        shippingStoreData?.id === Number(selectedPlatformId) ||
        erpStoreData?.id === Number(selectedPlatformId) ||
        omsStoreData?.id === Number(selectedPlatformId) ||
        ticketStoreData?.id === Number(selectedPlatformId)
      ) {
        setLoading(false)
        return;
      } else {
        checkAccess();
      }
    }
  }, [fetchedConnectedPlatformData, mainStoreData, accountingStoreData, shippingStoreData, erpStoreData, omsStoreData, ticketStoreData, fetchedConnectedPlatformLoading, location.pathname, selectedPlatformId])

  const handleSave = (): void => {
    setKey((prev) => prev + 1) // Force re-render of the DynamicPlatformSettingNew page
  }
 return loading ? (
   <Loader variant="content" />
 ) : (
   <PlatformAdditionalMappingGate selectedPlatformId={selectedPlatformId}>
     <DynamicPlatformSettingNew key={key} onSave={handleSave} />
   </PlatformAdditionalMappingGate>
 )
}

export default DynamicPlatformSettingWrapper
